import React, { useEffect, useState } from 'react';
import { useI18n } from '@/context/I18nContext';
import { useAuth } from '@/context/AuthContext';
import { rpc } from '@/api/client';
import { LanguageDropdown } from './LanguageDropdown';
import './login.css';

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={key++} className="font-semibold text-text-primary">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*') && token.endsWith('*')) {
      nodes.push(<em key={key++} className="italic text-text-primary">{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(<code key={key++} className="px-1.5 py-0.5 rounded bg-bg-raised text-xs font-mono text-text-primary">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        const isExternal = /^https?:\/\//i.test(href);
        nodes.push(
          <a
            key={key++}
            href={href}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            className="text-accent hover:underline"
          >
            {label}
          </a>
        );
      } else {
        nodes.push(token);
      }
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function renderBuiltInMarkdown(markdown: string): React.ReactNode {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let paragraphLines: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      const text = paragraphLines.join(' ');
      elements.push(
        <p key={key++} className="text-sm text-text-secondary leading-relaxed mb-3">
          {renderInlineMarkdown(text)}
        </p>
      );
      paragraphLines = [];
    }
  };

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={key++} className="list-disc pl-5 space-y-1.5 mb-3 text-sm text-text-secondary">
          {listItems.map((item, idx) => (
            <li key={idx} className="leading-relaxed">
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    if (line === '---' || line === '***') {
      flushParagraph();
      flushList();
      elements.push(<hr key={key++} className="my-4 border-border-subtle" />);
      continue;
    }
    if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      elements.push(
        <h1 key={key++} className="text-xl font-bold text-text-primary mt-2 mb-3">
          {renderInlineMarkdown(line.slice(2).trim())}
        </h1>
      );
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      elements.push(
        <h2 key={key++} className="text-base font-bold text-text-primary mt-5 mb-2">
          {renderInlineMarkdown(line.slice(3).trim())}
        </h2>
      );
      continue;
    }
    if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
      elements.push(
        <h3 key={key++} className="text-sm font-semibold text-text-primary mt-4 mb-1.5">
          {renderInlineMarkdown(line.slice(4).trim())}
        </h3>
      );
      continue;
    }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      flushParagraph();
      listItems.push(line.slice(2).trim());
      continue;
    }
    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();

  return <div className="markdown-body">{elements}</div>;
}

export interface TermsAcceptanceGateProps {
  /** Optional bundled markdown fallback by locale when initial RPC is loading. */
  fallbackMarkdown?: Partial<Record<string, string>>;
  /** Optional custom markdown renderer (e.g. shared <Markdown />). */
  renderMarkdown?: (markdown: string) => React.ReactNode;
  /** Optional callback invoked after terms acceptance succeeds and user state is refreshed. */
  onAccepted?: () => Promise<void> | void;
  /** Optional custom sign-out handler. Defaults to useAuth().signOut. */
  onSignOut?: () => void;
}

interface GetTermsResponse {
  markdown: string;
  hash: string;
  locale: string;
}

const VALID_TERMS_LOCALES = new Set(['en', 'de', 'fr', 'es', 'it', 'nl']);

export function TermsAcceptanceGate({
  fallbackMarkdown,
  renderMarkdown,
  onAccepted,
  onSignOut,
}: TermsAcceptanceGateProps) {
  const { t, locale, setLocale } = useI18n();
  const { user, signOut, refreshUser } = useAuth();

  const normalizedLocale = VALID_TERMS_LOCALES.has(locale) ? locale : 'en';
  const [markdown, setMarkdown] = useState<string>(fallbackMarkdown?.[normalizedLocale] ?? fallbackMarkdown?.en ?? '');
  const [termsHash, setTermsHash] = useState<string>(user?.termsHash ?? '');
  const [loadingDoc, setLoadingDoc] = useState<boolean>(!(fallbackMarkdown?.[normalizedLocale] ?? fallbackMarkdown?.en));
  const [checked, setChecked] = useState<boolean>(false);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isUpdatedVersion = Boolean(
    user?.acceptedTermsHash &&
      user.acceptedTermsHash.trim() !== '' &&
      user.acceptedTermsHash !== (termsHash || user?.termsHash)
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (fallbackMarkdown?.[normalizedLocale]) {
      setMarkdown(fallbackMarkdown[normalizedLocale]);
    } else {
      setLoadingDoc(true);
    }

    rpc<GetTermsResponse>('GetTermsAndConditions', { locale: normalizedLocale })
      .then((res) => {
        if (cancelled) return;
        if (res?.markdown) {
          setMarkdown(res.markdown);
        }
        if (res?.hash) {
          setTermsHash(res.hash);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch Terms and Conditions:', err);
        if (!fallbackMarkdown?.[normalizedLocale]) {
          setError(t('terms.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDoc(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedLocale, fallbackMarkdown, t]);

  const handleAccept = async () => {
    const hashToAccept = termsHash || user?.termsHash || '';
    if (!checked || !hashToAccept || accepting) return;

    setAccepting(true);
    setError(null);
    try {
      await rpc('AcceptTermsAndConditions', { hash: hashToAccept });
      await refreshUser();
      if (onAccepted) {
        await onAccepted();
      }
    } catch (err: any) {
      console.error('Failed to accept Terms and Conditions:', err);
      setError(t('terms.acceptError'));
    } finally {
      setAccepting(false);
    }
  };

  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
    } else {
      signOut();
    }
  };

  const effectiveHash = termsHash || user?.termsHash || '';
  const bodyMarkdown = markdown.replace(/^\s*#\s+[^\n]+\n+/, '');

  return (
    <div className="login-page !justify-start" data-testid="terms-acceptance-gate">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="login-bg-grid" />
        <div className="login-bg-glow" />
      </div>

      <div className="relative z-10 w-full max-w-3xl px-4 py-6">
        <div className="rounded-2xl border border-border-default bg-bg-surface/95 backdrop-blur-md shadow-2xl">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 px-6 py-5 border-b border-border-subtle bg-bg-raised/60 rounded-t-2xl">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-text-primary">
                {t('terms.gateTitle')}
              </h1>
              <p className="text-xs text-text-muted mt-0.5">
                {t('terms.gateSubtitle')}
              </p>
            </div>

            {setLocale && (
              <div className="flex items-center shrink-0">
                <LanguageDropdown
                  locale={locale}
                  onChange={setLocale}
                  testIdPrefix="terms-locale"
                />
              </div>
            )}
          </div>

          {/* Updated Terms Banner */}
          {isUpdatedVersion && (
            <div
              data-testid="terms-updated-banner"
              className="px-6 py-3 bg-amber-500/10 border-b border-amber-500/25 flex items-start gap-3"
            >
              <svg className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zm9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h-14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              <div className="text-xs text-amber-200 leading-relaxed">
                <span className="font-semibold text-amber-300">{t('terms.updatedBannerTitle')}</span>{' '}
                {t('terms.updatedBannerDesc')}
              </div>
            </div>
          )}

          {/* Full-Height Markdown Content (no inner scrollbar; user scrolls page to bottom) */}
          <div
            data-testid="terms-markdown-container"
            className="px-6 py-5 bg-bg-base/40"
          >
            {loadingDoc && !markdown ? (
              <div className="py-12 text-center text-sm text-text-muted">
                {t('common.loading')}
              </div>
            ) : renderMarkdown ? (
              renderMarkdown(bodyMarkdown)
            ) : (
              renderBuiltInMarkdown(bodyMarkdown)
            )}
          </div>

          {/* Footer / Acceptance Controls */}
          <div className="px-6 py-5 border-t border-border-subtle bg-bg-raised/60 rounded-b-2xl flex flex-col gap-5">
            {error && (
              <div
                data-testid="terms-error-banner"
                className="px-3.5 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300"
              >
                {error}
              </div>
            )}

            {/* Separate row for the acceptance checkbox */}
            <label className="flex items-start sm:items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                data-testid="terms-accept-checkbox"
                className="mt-0.5 sm:mt-0 w-4 h-4 rounded border-border-default bg-bg-base text-accent focus:ring-accent cursor-pointer shrink-0"
              />
              <span className="text-xs sm:text-sm text-text-primary font-medium leading-relaxed">
                {t('terms.checkboxLabel')}
              </span>
            </label>

            {/* Action buttons row (Sign Out directly to the left of Accept & Continue) */}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleSignOut}
                data-testid="terms-signout-button"
                className="px-3 py-2 text-xs sm:text-sm font-medium text-text-muted hover:text-text-primary transition-colors cursor-pointer shrink-0"
              >
                {t('auth.signOut')}
              </button>

              <button
                type="button"
                onClick={handleAccept}
                disabled={!checked || !effectiveHash || accepting || loadingDoc}
                data-testid="terms-accept-button"
                className="px-5 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-accent text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity cursor-pointer shrink-0"
              >
                {accepting ? t('terms.acceptingButton') : t('terms.acceptButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
